import { Box, Badge, ExpandLessIcon, ExpandMoreIcon, IconButton, SearchIcon, FilterAltOutlinedIcon,
    Typography, SearchBar } from 'src/components';
import { useIsMobile } from 'src/hooks';
import { useState, useEffect } from 'react';
import { useFeatureTravellerStore } from 'src/stores';

export default function TopBar({
    children,
    searchValue,
    setSearchValue,
    activeFilterCount,
    isExpanded,
    setIsExpanded,
    handleCurrentViewChange
}) {

    const isMobile = useIsMobile();
    const desktopBarWidth = isExpanded ? '430px' : '300px';
    const desktopBarPadding = isExpanded ? 2 : 1;
    const expandedHeight = 'calc(100vh - 120px)';
    const collapsedHeight = isMobile ? 'min-content' : '50px';

    const [showChildren, setShowChildren] = useState(isExpanded);
    const setSelectedTraveller = useFeatureTravellerStore((state) => state.setSelectedTraveller);
    const setSelectedFeature = useFeatureTravellerStore((state) => state.setSelectedFeature);

    useEffect(() => {
        const timeout = setTimeout(() => setShowChildren(isExpanded), 100);
        return () => clearTimeout(timeout);
    }, [isExpanded]);

    const handleTopBarClick = (newView) => {
        handleCurrentViewChange(newView);
        if (!isExpanded) {
            setIsExpanded(true);
        }
        setSelectedFeature(null);
        setSelectedTraveller(null);
    };

    const toggleIsExpanded = () => {
        setSelectedFeature(null);
        setSelectedTraveller(null);
        setIsExpanded(!isExpanded);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                p: isMobile ? 3 : desktopBarPadding,
                paddingBottom: 1,
                mt: isMobile ? 'var(--safe-area-top)' : 3,
                mx: isMobile ? 0 : 3,
                mb: isMobile ? 0 : 3,
                background: isMobile ? 'transparent ': 'white',
                width: isMobile ? '100%' : desktopBarWidth,
                height: isExpanded ? expandedHeight : collapsedHeight,
                overflow: 'hidden',
                borderRadius: 8,
                boxShadow: isMobile ? '' : '0 4px 20px rgba(0,0,0,0.05)',
                willChange: 'height',
                transition: 'all 0.4s ease-in-out',
                pointerEvents: 'auto'
            }}>
            
            <Box sx={{
                    display: 'flex', 
                    flexDirection: 'row', 
                    position: 'sticky',
                    gap: 1,
                    flexShrink: 0}}>

                {!isMobile ? (        
                <SearchBar
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    handleSearchFocus={() => handleTopBarClick("Search")}
                    handleExitSearch={() => handleTopBarClick("AttractionsTravellersList")}
                    />
                ): 
                <Box
                    sx={{
                        bgcolor: 'white',
                        borderRadius: 100,
                        border: '1px solid #D3D3D3',
                        py: 0.8,
                        px: 2,
                        flexGrow: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                    onClick={() => handleTopBarClick("Search")}>
                    <SearchIcon fontSize="small" sx={{color: "text.secondary"}} />
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {searchValue || 'Search...'}
                    </Typography>
                </Box>}

                <IconButton
                    onClick={() => handleTopBarClick("Filters")}
                    size="small"
                    disableRipple
                    sx={{
                        background: isMobile ? 'white' : 'transparent',
                        width: 35,
                        '& .MuiBadge-badge': {
                            minWidth: 14,
                            height: 14,
                            fontSize: '0.6rem',
                            padding: '0 4px'
                        }
                    }}>
                    <Badge badgeContent={activeFilterCount} color="primary">
                        <FilterAltOutlinedIcon fontSize="small"/>
                    </Badge>
                </IconButton>
                
                {!isMobile && (
                    <IconButton onClick={toggleIsExpanded} size="small" disableRipple>
                        {isExpanded ? <ExpandLessIcon fontSize="small" />
                        : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                )}
            </Box>

            {isExpanded && !isMobile && (
                <Box sx={{ 
                    height: '100%', 
                    overflowY: 'auto', 
                    marginTop: 2,
                    marginRight: 1,
                    marginBottom: 1,
                    opacity: showChildren ? 1 : 0,
                    transition: 'opacity 0.4s ease-in-out',
                    pointerEvents: showChildren ? 'auto' : 'none'}}>

                    {showChildren ? children : null}

                </Box>
            )}

        </Box>
    );
}
